import {useEffect, useRef, useState, type ReactNode} from 'react'
import {useTranslation} from 'react-i18next'
import {Input} from '@/components/ui/input'
import {Label} from '@/components/ui/label'
import {HelpIcon} from '@/components/help-icon'

const OTHER = '__otra__'

/** The backend accepts any non-empty string (`validate_arl`/`validate_eps`); only the picker differs. */
export function CatalogOtherField({
  id,
  label,
  help,
  options,
  value,
  onChange,
  onCommit,
  hint,
  disabled,
}: {
  id: string
  label: string
  help?: ReactNode
  options: readonly string[]
  value: string
  /** Local draft update, fired on every keystroke — never persists by itself. */
  onChange: (value: string) => void
  /** Persists: fired immediately on a select pick, or on the free-text input's blur. */
  onCommit: (value: string) => void
  /** Always-visible helper line under the field; not a placeholder. */
  hint?: string
  disabled?: boolean
}) {
  const {t} = useTranslation()
  const [otherMode, setOtherMode] = useState(() => value !== '' && !options.includes(value))
  // The catalog often loads after mount: reconcile «Otra» once, when it first becomes non-empty,
  // never per keystroke.
  const reconciled = useRef(options.length > 0)
  useEffect(() => {
    if (reconciled.current || options.length === 0) return
    reconciled.current = true
    if (value !== '' && options.includes(value)) setOtherMode(false)
  }, [value, options])

  function handleSelectChange(next: string) {
    if (next === OTHER) {
      setOtherMode(true)
      onChange('')
    } else {
      setOtherMode(false)
      onChange(next)
      onCommit(next)
    }
  }

  return (
    <div className="space-y-2">
      <span className="flex items-center gap-1.5">
        <Label htmlFor={id}>{label}</Label>
        {help && (
          <HelpIcon label={t('common.help.iconLabelFor', {campo: label})} testId={`${id}-help`}>
            {help}
          </HelpIcon>
        )}
      </span>
      <select
        id={id}
        value={otherMode ? OTHER : value}
        onChange={(e) => handleSelectChange(e.target.value)}
        disabled={disabled}
        className="h-11 w-full rounded-lg border border-input bg-transparent px-2.5 text-base text-foreground md:h-9 md:text-sm">
        <option value="">{t('catalogOther.unspecified')}</option>
        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
        <option value={OTHER}>{t('catalogOther.other')}</option>
      </select>
      {hint && (
        <p className="text-xs text-muted-foreground" data-testid={`${id}-hint`}>
          {hint}
        </p>
      )}
      {otherMode && (
        <Input
          id={`${id}-other`}
          data-testid={`${id}-other-input`}
          value={value}
          maxLength={200}
          placeholder={t('catalogOther.otherPlaceholder')}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          onBlur={(e) => onCommit(e.target.value)}
        />
      )}
    </div>
  )
}
